import { useRef } from "react";
import { toast } from "sonner";
import { Copy, Share2, Upload, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ShopLogo } from "@/components/ShopLogo";

const MAX_LOGO = 2 * 1024 * 1024;
const TYPES = ["image/jpeg", "image/png", "image/webp"];

export const LogoField = ({ name, logo, onUpload, onRemove }) => {
  const input = useRef(null);
  const pick = (file) => {
    if (!file) return;
    if (!TYPES.includes(file.type)) return toast.error("Format non pris en charge (JPEG, PNG ou WEBP)");
    if (file.size > MAX_LOGO) return toast.error("Logo trop volumineux (2 Mo maximum)");
    onUpload(file);
  };
  return (
    <div className="space-y-2 sm:col-span-2" data-testid="shop-logo-field">
      <Label>Logo de la boutique</Label>
      <div className="flex flex-wrap items-center gap-4">
        <ShopLogo shop={{ name, logo }} testid="shop-logo-preview" className="h-20 w-20 rounded-2xl text-2xl" />
        <div className="flex flex-wrap gap-2">
          <input ref={input} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" data-testid="shop-logo-input" onChange={(e) => { pick(e.target.files[0]); e.target.value = ""; }} />
          <Button type="button" variant="outline" className="rounded-full" onClick={() => input.current.click()} data-testid="shop-logo-upload-btn"><Upload className="mr-2 h-4 w-4" />{logo ? "Remplacer" : "Importer un logo"}</Button>
          {logo && <Button type="button" variant="ghost" className="rounded-full text-red-700" onClick={onRemove} data-testid="shop-logo-remove-btn"><Trash2 className="mr-2 h-4 w-4" />Retirer</Button>}
        </div>
      </div>
      <p className="text-xs text-gray-500">JPEG, PNG ou WEBP, 2 Mo maximum. Le logo est affiché en entier, sans rognage. Sans logo, les initiales de la boutique sont affichées. N'oubliez pas d'enregistrer.</p>
    </div>
  );
};

export const ShareShop = ({ shop }) => {
  const url = `${window.location.origin}/boutique/${shop.slug}`;
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); toast.success("Lien copié"); } catch { window.prompt("Copiez le lien de votre boutique :", url); }
  };
  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: shop.name, text: `Découvrez ma boutique ${shop.name} sur Casadeal`, url }); } catch { /* partage annulé */ }
    } else window.open(`https://wa.me/?text=${encodeURIComponent(`Découvrez ma boutique ${shop.name} sur Casadeal : ${url}`)}`, "_blank", "noopener");
  };
  return (
    <div data-testid="share-shop" className="space-y-3 rounded-2xl border bg-white p-5">
      <h2 className="font-display text-lg font-bold">Lien de ma boutique</h2>
      <p data-testid="share-shop-url" className="break-all rounded-lg bg-gray-50 px-3 py-2 font-mono text-sm">{url}</p>
      <div className="flex flex-wrap gap-2">
        <Button type="button" className="rounded-full" onClick={copy} data-testid="copy-shop-link-btn"><Copy className="mr-2 h-4 w-4" />Copier le lien de ma boutique</Button>
        <Button type="button" variant="outline" className="rounded-full" onClick={share} data-testid="share-shop-btn"><Share2 className="mr-2 h-4 w-4" />Partager ma boutique</Button>
      </div>
      <p className="text-xs text-gray-500">{shop.visible ? "Ce lien reste le même si vous changez le nom, le logo ou les produits." : "Ce lien sera accessible au public uniquement après validation de votre boutique et activation de votre abonnement."}</p>
    </div>
  );
};
